import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * Admin → Organisation → Billing tab.
 *
 * Shows current plan, usage progress bars, trial/renewal date,
 * and a "Manage billing" button that opens the in-app billing
 * management view (subscription state, invoices, cancel) — Mollie
 * doesn't ship a hosted Customer Portal so we host it ourselves.
 *
 * If Mollie isn't configured yet (test envs), we still render the
 * plan + usage data and just hide the portal button.
 */
export function BillingTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const d = await api.getBillingSubscription();
      setData(d);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function openPortal() {
    setPortalLoading(true);
    try {
      const { url } = await api.openBillingPortal();
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || 'Could not open billing portal');
    } finally {
      setPortalLoading(false);
    }
  }

  if (loading) {
    return <div style={pad} data-testid="billing-tab-loading">Loading billing info…</div>;
  }
  if (error) {
    return <div style={{ ...pad, color: '#a23' }} data-testid="billing-tab-error">⚠️ {error}</div>;
  }
  if (!data) return null;

  const statusBadge = (() => {
    const map = {
      trialing: { label: '🎁 Trial', bg: '#fff4d6', fg: '#7a5a13' },
      active: { label: '✓ Active', bg: '#dbf5ef', fg: '#0b6b58' },
      past_due: { label: '⚠️ Past due', bg: '#fde4e4', fg: '#8a2929' },
      canceled: { label: '✗ Canceled', bg: '#f1f5f4', fg: '#666' },
      none: { label: '— No subscription', bg: '#f1f5f4', fg: '#666' },
    };
    return map[data.status] || map.none;
  })();

  return (
    <div data-testid="billing-tab" style={{ padding: '1rem 0' }}>
      {/* Plan card */}
      <div style={card} data-testid="billing-plan-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <small style={kicker}>Current plan</small>
            <h2 style={h2}>{data.plan_name}</h2>
            <span
              style={{
                ...statusPill,
                background: statusBadge.bg,
                color: statusBadge.fg,
              }}
              data-testid="billing-status-badge"
            >
              {statusBadge.label}
            </span>
            {data.trial_end && data.status === 'trialing' && (
              <p style={meta}>
                Trial ends <strong>{new Date(data.trial_end).toLocaleDateString()}</strong>
              </p>
            )}
            {data.current_period_end && data.status === 'active' && (
              <p style={meta}>
                Next invoice: <strong>{new Date(data.current_period_end).toLocaleDateString()}</strong>
                {data.cancel_at_period_end && ' (will cancel)'}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
            {(data.billing_configured ?? data.stripe_configured) && (data.mollie_customer_id || data.stripe_customer_id) ? (
              <button
                type="button"
                onClick={openPortal}
                disabled={portalLoading}
                style={primaryBtn}
                data-testid="billing-portal-btn"
              >
                {portalLoading ? 'Opening…' : '🔐 Manage billing'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                style={primaryBtn}
                data-testid="billing-go-pricing"
              >See plans →</button>
            )}
            {!(data.billing_configured ?? data.stripe_configured) && (
              <small style={{ fontSize: '0.74rem', color: '#a85a18' }}>
                Mollie not yet configured
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Usage card */}
      <div style={card} data-testid="billing-usage-card">
        <small style={kicker}>Usage</small>
        <h3 style={h3}>This month</h3>
        <div style={usageGrid}>
          {[
            { key: 'max_children', label: 'Children', usage: data.usage.children, limit: data.limits.max_children },
            { key: 'max_staff', label: 'Staff', usage: data.usage.staff, limit: data.limits.max_staff },
            { key: 'max_centers', label: 'Centres', usage: data.usage.centers, limit: data.limits.max_centers },
            { key: 'recap_videos_per_month', label: 'Recap videos', usage: data.usage.recap_videos_this_month, limit: data.limits.recap_videos_per_month },
          ].map((row) => {
            const unlimited = row.limit == null;
            const pct = unlimited ? 0 : Math.min(100, Math.round((row.usage / Math.max(row.limit, 1)) * 100));
            const hardish = !unlimited && pct >= 100;
            const warnish = !unlimited && pct >= 80 && pct < 100;
            return (
              <div key={row.key} style={usageRow} data-testid={`billing-usage-${row.key}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <strong style={{ fontSize: '0.88rem' }}>{row.label}</strong>
                  <span style={{ fontSize: '0.82rem', color: hardish ? '#8a2929' : warnish ? '#7a5a13' : '#446f6b' }}>
                    {row.usage} / {unlimited ? '∞' : row.limit}
                  </span>
                </div>
                <div style={progressTrack}>
                  <div
                    style={{
                      ...progressFill,
                      width: unlimited ? '100%' : `${pct}%`,
                      background: hardish ? '#d44' : warnish ? '#d9a233' : 'hsl(189 53% 35%)',
                      opacity: unlimited ? 0.3 : 1,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div style={{ ...card, background: '#f6fbfa', borderStyle: 'dashed' }}>
        <small style={kicker}>Need to change plan?</small>
        <p style={{ margin: '0.3rem 0 0.7rem 0', color: '#456a67', fontSize: '0.88rem' }}>
          Upgrade, downgrade, or cancel any time — your data and settings stay intact.
        </p>
        <button
          type="button"
          onClick={() => navigate('/pricing')}
          style={secondaryBtn}
          data-testid="billing-view-pricing"
        >View all plans →</button>
      </div>
    </div>
  );
}

const pad = { padding: '1.5rem 0', textAlign: 'center', color: '#5d7f7c' };
const card = {
  background: '#fff',
  border: '1px solid #e3eeec',
  borderRadius: 14,
  padding: '1.2rem 1.3rem',
  marginBottom: '1rem',
};
const kicker = {
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: '#6b8c89',
  fontWeight: 700,
};
const h2 = { fontSize: '1.6rem', fontWeight: 700, margin: '0.2rem 0 0.4rem 0', color: '#0b3d3a' };
const h3 = { fontSize: '1.1rem', fontWeight: 700, margin: '0.2rem 0 1rem 0', color: '#0b3d3a' };
const statusPill = {
  display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999,
  fontSize: '0.74rem', fontWeight: 700, marginBottom: '0.4rem',
};
const meta = { margin: '0.35rem 0 0 0', color: '#446f6b', fontSize: '0.86rem' };
const primaryBtn = {
  padding: '0.55rem 1.1rem', borderRadius: 10,
  background: 'hsl(189 53% 25%)', color: '#fff', border: 'none',
  fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
};
const secondaryBtn = {
  padding: '0.5rem 1rem', borderRadius: 10,
  background: '#fff', color: 'hsl(189 53% 25%)',
  border: '1px solid hsl(189 53% 25%)',
  fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
};
const usageGrid = { display: 'flex', flexDirection: 'column', gap: '0.85rem' };
const usageRow = {};
const progressTrack = {
  height: 6, background: '#e9f0ef', borderRadius: 999, overflow: 'hidden',
};
const progressFill = {
  height: '100%', borderRadius: 999, transition: 'width 250ms ease, background 250ms ease',
};

export default BillingTab;
